/**
 * ItemSnowflakeQueryPanel
 *
 * Runs a read-only warehouse query (the engine rejects writes/DDL) and lets the
 * user attach the result as first-class Evidence on this item. Import / read-only.
 *
 * Rendered in the Evidence tab of ItemDetailPage.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronUp,
  Database,
  Loader2,
  Paperclip,
  Play,
} from 'lucide-react';
import {
  getIntegrationsApi,
  type SnowflakeQueryResult,
} from '../../../lib/integrationsApi';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useEvidenceStore } from '../../../stores/prioritization/evidenceStore';

const SnowflakeIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect width="24" height="24" rx="4" fill="#29B5E8" />
    <line x1="12" y1="4" x2="12" y2="20" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="4" y1="12" x2="20" y2="12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="6.3" y1="6.3" x2="17.7" y2="17.7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="17.7" y1="6.3" x2="6.3" y2="17.7" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="12" cy="12" r="2" fill="white" />
  </svg>
);

const ROW_LIMIT = 100;
const PREVIEW_ROWS = 20;

interface ItemSnowflakeQueryPanelProps {
  itemId: string;
  itemTitle?: string;
}

function cellToString(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function ItemSnowflakeQueryPanel({ itemId, itemTitle }: ItemSnowflakeQueryPanelProps) {
  const workspaceId = getCurrentWorkspaceId();
  const addEvidence = useEvidenceStore((s) => s.addEvidence);

  const [isOpen, setIsOpen] = useState(false);
  const [connected, setConnected] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [statement, setStatement] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<SnowflakeQueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attached, setAttached] = useState(false);

  const checkStatus = useCallback(async () => {
    if (!workspaceId) return;
    setChecking(true);
    try {
      const status = await getIntegrationsApi(workspaceId).getSnowflakeStatus();
      setConnected(status.connected);
    } catch {
      setConnected(false);
    } finally {
      setChecking(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (isOpen && connected === null && !checking) void checkStatus();
  }, [isOpen, connected, checking, checkStatus]);

  const runQuery = async () => {
    if (!workspaceId || !statement.trim()) return;
    setRunning(true);
    setError(null);
    setResult(null);
    setAttached(false);
    try {
      const res = await getIntegrationsApi(workspaceId).runSnowflakeQuery(statement.trim(), {
        limit: ROW_LIMIT,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Query failed');
    } finally {
      setRunning(false);
    }
  };

  const handleAttach = () => {
    if (!result) return;
    const previewRows = result.rows.slice(0, PREVIEW_ROWS);
    const colNames = result.columns.map((c) => c.name);
    addEvidence({
      itemId,
      type: 'data',
      reliability: 'verified',
      accessLevel: 'internal',
      source: 'Snowflake',
      sourceType: 'data warehouse',
      supportsClaim: 'Warehouse-derived business signal',
      summary: `Snowflake query returned ${result.rowCount} row${result.rowCount === 1 ? '' : 's'} (${colNames.join(', ') || 'no columns'}).`,
      rawContent: JSON.stringify(
        {
          statement: statement.trim(),
          columns: result.columns,
          rowCount: result.rowCount,
          rows: previewRows,
          truncated: result.rows.length > PREVIEW_ROWS,
        },
        null,
        2,
      ),
      tags: ['snowflake', 'warehouse'],
    });
    setAttached(true);
    setTimeout(() => setAttached(false), 1800);
  };

  const previewRows = result?.rows.slice(0, PREVIEW_ROWS) ?? [];

  return (
    <div className="pt-3 border-t border-[var(--rule)]">
      {/* Section header */}
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[var(--text-ter)] dark:text-[var(--text-ter)] uppercase tracking-wide flex items-center gap-1.5">
          <SnowflakeIcon className="w-3.5 h-3.5" />
          Snowflake warehouse query
        </p>
        <button
          onClick={() => setIsOpen((v) => !v)}
          className="flex items-center gap-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-ter)] dark:hover:text-[var(--text-ter)] transition-colors"
        >
          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          {isOpen ? 'Hide' : 'Run a query'}
        </button>
      </div>

      {!isOpen && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic py-0.5">
          Enrich this item with usage, revenue, or custom warehouse signals.
        </p>
      )}

      {isOpen && (
        <div className="mt-1 border border-[#29B5E8]/30 dark:border-[#29B5E8]/40 bg-[var(--bg-raised)] dark:bg-[var(--surface)] shadow-sm overflow-hidden">
          <div className="p-3 space-y-3">
            {checking && (
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] py-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Checking connection…
              </div>
            )}

            {/* Not connected */}
            {!checking && connected === false && (
              <div className="flex items-start gap-2 p-2.5 bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border border-[var(--mustard)] dark:border-[var(--mustard)]">
                <AlertCircle className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <p className="text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
                  Snowflake is not connected. Go to{' '}
                  <a href="/prioritization/settings/connectors" className="underline font-medium">
                    Settings → Connectors
                  </a>{' '}
                  to connect your warehouse.
                </p>
              </div>
            )}

            {connected && (
              <>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-1">
                    Read-only query{' '}
                    <span className="text-[var(--text-ter)] font-normal">
                      (SELECT / WITH / SHOW / DESCRIBE / EXPLAIN only)
                    </span>
                  </label>
                  <textarea
                    value={statement}
                    onChange={(e) => setStatement(e.target.value)}
                    rows={3}
                    spellCheck={false}
                    placeholder={
                      itemTitle
                        ? `SELECT metric, value FROM analytics.feature_usage\nWHERE feature = '${itemTitle.slice(0, 24)}'`
                        : 'SELECT ... FROM your_table WHERE ...'
                    }
                    className="w-full px-2.5 py-2 text-xs font-mono border border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 focus:outline-none focus:border-[#29B5E8] focus:ring-2 focus:ring-[#29B5E8]/20 resize-y"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[var(--text-ter)]">Up to {ROW_LIMIT} rows</span>
                  <button
                    type="button"
                    onClick={() => void runQuery()}
                    disabled={running || !statement.trim()}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#29B5E8] hover:bg-[#1f9fd0] text-white"
                  >
                    {running ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Running…
                      </>
                    ) : (
                      <>
                        <Play className="h-3 w-3" />
                        Run query
                      </>
                    )}
                  </button>
                </div>

                {/* Error (includes read-only guard rejections) */}
                {error && (
                  <div className="flex items-start gap-1.5 text-xs text-[var(--coral)] dark:text-[var(--coral)] p-2 bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}

                {/* Result table */}
                {result && (
                  <div className="space-y-2">
                    {result.columns.length === 0 || result.rowCount === 0 ? (
                      <p className="text-xs text-[var(--text-ter)] italic">
                        Query succeeded — no rows returned.
                      </p>
                    ) : (
                      <div className="border border-[var(--rule)] overflow-x-auto max-h-60 overflow-y-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead className="sticky top-0 bg-[var(--surface)] dark:bg-[var(--bg-raised)]/80">
                            <tr>
                              {result.columns.map((col) => (
                                <th
                                  key={col.name}
                                  className="text-left font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)] px-2 py-1.5 border-b border-[var(--rule)] whitespace-nowrap"
                                  title={col.type}
                                >
                                  {col.name}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {previewRows.map((row, ri) => (
                              <tr
                                key={ri}
                                className="border-b border-[var(--rule)] last:border-0"
                              >
                                {result.columns.map((col) => (
                                  <td
                                    key={col.name}
                                    className="px-2 py-1 text-[var(--text-sec)] dark:text-[var(--text-sec)] font-mono whitespace-nowrap max-w-[16rem] truncate"
                                    title={cellToString(row[col.name])}
                                  >
                                    {cellToString(row[col.name])}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-[var(--text-ter)]">
                        {result.rowCount} row{result.rowCount === 1 ? '' : 's'}
                        {result.rows.length > PREVIEW_ROWS
                          ? ` · showing first ${PREVIEW_ROWS}`
                          : ''}
                      </span>
                      <button
                        type="button"
                        onClick={handleAttach}
                        disabled={attached}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 bg-[var(--slate)] hover:bg-[var(--slate)] text-white"
                      >
                        {attached ? (
                          <>
                            <Check className="h-3 w-3" />
                            Attached
                          </>
                        ) : (
                          <>
                            <Paperclip className="h-3 w-3" />
                            Attach as evidence
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {!result && !error && !running && (
                  <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] italic flex items-center gap-1.5">
                    <Database className="h-3 w-3" />
                    Write a read-only query and run it to preview results.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
